import React, { useState, useMemo } from 'react';
import { Table, Button, Badge, Card, Form } from 'react-bootstrap';
import { FaTrash, FaEdit, FaCheckCircle, FaUsers, FaSort, FaSortUp, FaSortDown, FaUser } from 'react-icons/fa';
import Message from '../../components/Message';
import Loader from '../../components/Loader';
import { useDeleteUserMutation, useGetUsersQuery } from '../../slices/usersApiSlice';
import { toast } from 'react-toastify';
import { Link } from 'react-router-dom';

const UserListScreen = () => {
    const { data: users = [], refetch, isLoading, error } = useGetUsersQuery();
    const [deleteUser] = useDeleteUserMutation();

    // --- STATES ---
    const [search, setSearch] = useState('');
    const [sortConfig, setSortConfig] = useState({ key: 'name', direction: 'asc' });
    const [currentPage, setCurrentPage] = useState(1);
    const [itemsPerPage, setItemsPerPage] = useState(25);

    // --- DELETE HANDLER ---
    const deleteHandler = async (id, name) => {
        if (window.confirm(`Weet je zeker dat je ${name} wilt verwijderen?`)) {
        try {
            await deleteUser(id).unwrap();
            toast.success('Gebruiker verwijderd');
            refetch();
        } catch (err) {
            toast.error(err?.data?.message || err.error);
        }
        }
    };

    const filteredUsers = useMemo(() => {
        let list = [...users];

        // 1️ Search filter
        if (search) {
            const q = search.toLowerCase();
            list = list.filter(
            (u) =>
                u.name.toLowerCase().includes(q) ||
                u.email.toLowerCase().includes(q)
            );
        }

        // 2️ Single sort: admins first, then column sort
        list.sort((a, b) => {
            // Admins always on top
            if (a.isAdmin && !b.isAdmin) return -1;
            if (!a.isAdmin && b.isAdmin) return 1;

            // Column sorting (skip if key is isAdmin)
            const { key, direction } = sortConfig;
            if (key === 'isAdmin') return 0;

            let valA = a[key];
            let valB = b[key];

            if (typeof valA === 'boolean') valA = valA ? 1 : 0;
            if (typeof valB === 'boolean') valB = valB ? 1 : 0;

            if (valA < valB) return direction === 'asc' ? -1 : 1;
            if (valA > valB) return direction === 'asc' ? 1 : -1;
            return 0;
        });

        return list;
    }, [users, search, sortConfig]);

    // --- PAGINATION ---
    const pageCount = Math.ceil(filteredUsers.length / itemsPerPage);
    const paginatedUsers = filteredUsers.slice(
        (currentPage - 1) * itemsPerPage,
        currentPage * itemsPerPage
    );

    // --- SORT HANDLER ---
    const handleSort = (key) => {
        setSortConfig((prev) => ({
        key,
        direction: prev.key === key && prev.direction === 'asc' ? 'desc' : 'asc',
        }));
    };

    const renderSortIcon = (key) => {
        if (sortConfig.key !== key) return <FaSort />;
        return sortConfig.direction === 'asc' ? <FaSortUp /> : <FaSortDown />;
    };

    return (
        <>
            {/* PAGE HEADER */}
            <div className="d-flex align-items-center justify-content-center mb-4">
                <h1 className="display-6 d-flex align-items-center gap-2">
                    <FaUsers /> Gebruikers
                </h1>
            </div>
            <span className="text-muted">Totaal: {users.length}</span>

            {/* SEARCH BAR */}
            <div className="mb-3" style={{ maxWidth: 600, margin: '0 auto' }}>
            <Form.Control
                placeholder="Zoek op naam of e-mail..."
                value={search}
                onChange={(e) => {
                setSearch(e.target.value);
                setCurrentPage(1);
                }}
            />
            </div>

            {isLoading ? (
                <Loader />
            ) : error ? (
                <Message variant="danger">{error?.data?.message || error.error}</Message>
            ) : (
                <>
                    <Card className="shadow-sm border-0">
                        <Card.Body className="p-0">
                        <Table hover responsive className="mb-0 align-middle">
                            <thead className="table-light">
                            <tr>
                                <th onClick={() => handleSort('name')} style={{ cursor: 'pointer' }}>
                                Naam {renderSortIcon('name')}
                                </th>
                                <th onClick={() => handleSort('email')} style={{ cursor: 'pointer' }}>
                                E-mail {renderSortIcon('email')}
                                </th>
                                <th className="text-center" onClick={() => handleSort('isAdmin')} style={{ cursor: 'pointer' }}>
                                Rol {renderSortIcon('isAdmin')}
                                </th>
                                <th className="text-center">Acties</th>
                            </tr>
                            </thead>
                            <tbody>
                            {paginatedUsers.map((user) => (
                                <tr key={user._id}>
                                <td className="fw-semibold">{user.name}</td>
                                <td>
                                    <a href={`mailto:${user.email}`} className="text-decoration-none">
                                    {user.email}
                                    </a>
                                </td>
                                <td className="text-center">
                                    {user.isAdmin ? (
                                    <Badge bg="success" className="px-3 py-2">
                                        <FaCheckCircle className="me-1" />
                                        Admin
                                    </Badge>
                                    ) : (
                                    <Badge bg="secondary" className="px-3 py-2">
                                        <FaUser className="me-1" />
                                        User
                                    </Badge>
                                    )}
                                </td>
                                <td className="text-end">
                                    {!user.isAdmin && (
                                    <div className="d-inline-flex gap-2">
                                        <Button
                                        as={Link}
                                        to={`/admin/user/${user._id}/edit`}
                                        variant="outline-primary"
                                        size="sm"
                                        >
                                        <FaEdit /> Bewerken
                                        </Button>
                                        <Button
                                        variant="outline-danger"
                                        size="sm"
                                        onClick={() => deleteHandler(user._id, user.name)}
                                        >
                                        <FaTrash /> Verwijderen
                                        </Button>
                                    </div>
                                    )}
                                </td>
                                </tr>
                            ))}
                            </tbody>
                        </Table>
                        </Card.Body>
                    </Card>

                    {/* PAGINATION */}
                    <div className="d-flex justify-content-between align-items-center mt-3">
                        <Button
                        size="sm"
                        disabled={currentPage === 1}
                        onClick={() => setCurrentPage((p) => p - 1)}
                        >
                        Vorige
                        </Button>

                        <span>
                        Pagina {currentPage} van {pageCount}
                        </span>

                        <Button
                        size="sm"
                        disabled={currentPage === pageCount}
                        onClick={() => setCurrentPage((p) => p + 1)}
                        >
                        Volgende
                        </Button>

                        <Form.Select
                        size="sm"
                        style={{ width: 100 }}
                        value={itemsPerPage}
                        onChange={(e) => {
                            setItemsPerPage(Number(e.target.value));
                            setCurrentPage(1);
                        }}
                        >
                        <option value={10}>10</option>
                        <option value={25}>25</option>
                        <option value={50}>50</option>
                        <option value={100}>100</option>
                        </Form.Select>

                        <small className="text-muted">
                        Toon {(currentPage - 1) * itemsPerPage + 1}–
                        {Math.min(currentPage * itemsPerPage, filteredUsers.length)} van {filteredUsers.length}
                        </small>
                    </div>
                </>
            )}
        </>
    );
};

export default UserListScreen;